import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ChildProfileProvider } from "./contexts/ChildProfileContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { SplashScreen } from "./screens/SplashScreen";
import { LoginScreen, RegisterScreen } from "./screens/AuthScreens";
import { PrivacyPolicyPage, TermsOfServicePage, ReConsentPage } from "./screens/LegalScreens";
import { ProfilesScreen } from "./screens/ProfilesScreen";
import { ChildHomeScreen } from "./screens/ChildHomeScreen";
import { WorldMapScreen } from "./screens/WorldMapScreen";
import { LevelSelectScreen } from "./screens/LevelSelectScreen";
import { GameplayScreen } from "./screens/GameplayScreen";
import { LevelCompleteScreen } from "./screens/LevelCompleteScreen";
import { ParentGateScreen } from "./screens/ParentScreens";
import { ComingSoonScreen } from "./screens/ComingSoonScreen";
import { HealthBadge } from "./design/HealthBadge";
import { ParentDashboardShell, ParentOverview } from "./parent/ParentDashboard";
import { ProfilesManager, ProgressDetail } from "./parent/ProfilesManager";
import { PurchasesScreen, PrivacyScreen, SupportScreen } from "./parent/OtherScreens";
import { WordBookScreen } from "./child/WordBookScreen";
import { RewardsRoomScreen } from "./screens/RewardsRoomScreen";
import { DailyFindScreen } from "./screens/DailyFindScreen";
import { LandingPage } from "./screens/LandingPage";
import { PurchaseGateScreen } from "./screens/PurchaseGateScreen";
import { OnboardingWizard } from "./screens/OnboardingWizard";
import { AdminShell } from "./admin/AdminShell";
import { AdminOverview } from "./admin/AdminOverview";
import { WorldManager, LevelManager } from "./admin/WorldLevelManager";
import { LevelEditor } from "./admin/LevelEditor";
import { DebugPreviews } from "./admin/DebugPreviews";
import { BackupsPage } from "./admin/BackupsPage";
import { LegalPacketPage } from "./admin/LegalPacketPage";
import { SecurityPage } from "./admin/SecurityPage";
import {
  AiSceneGenerator,
  MediaLibrary,
  LearningObjectLibrary,
  SupportInbox,
  AuditLog,
  AppSettingsEditor,
} from "./admin/AdminScreens";
import "@/design/tokens.css";
import "@/design/design-components.css";
import "@/design/screens.css";
import "@/parent/parent.css";

function RootRoute() {
  const { status } = useAuth();
  if (status === "checking") return <div className="wmleye-app" style={{ padding: 24 }}>Loading…</div>;
  if (status === "authed") return <SplashScreen />;
  return <LandingPage />;
}

function RequireAuth({ children }) {
  const { status, user, legalCurrent } = useAuth();
  if (status === "checking") return <div className="wmleye-app" style={{ padding: 24 }}>Loading…</div>;
  if (status !== "authed") return <Navigate to="/login" replace />;
  const acceptedPP = user?.privacyPolicyVersion || "";
  const acceptedTOS = user?.termsOfServiceVersion || "";
  if (legalCurrent
      && (legalCurrent.privacyPolicyVersion !== acceptedPP
          || legalCurrent.termsOfServiceVersion !== acceptedTOS)) {
    return <Navigate to="/legal/re-consent" replace />;
  }
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ChildProfileProvider>
          <SettingsProvider>
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/login" element={<LoginScreen />} />
              <Route path="/register" element={<RegisterScreen />} />
              <Route path="/register/setup" element={<RequireAuth><OnboardingWizard /></RequireAuth>} />
              <Route path="/parent/purchase" element={<RequireAuth><PurchaseGateScreen /></RequireAuth>} />
              <Route path="/parent/purchase/success" element={<RequireAuth><PurchaseGateScreen /></RequireAuth>} />
              <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/legal/terms" element={<TermsOfServicePage />} />
              <Route path="/legal/re-consent" element={<ReConsentPage />} />
              <Route
                path="/profiles"
                element={
                  <RequireAuth>
                    <ProfilesScreen />
                  </RequireAuth>
                }
              />
              <Route
                path="/child/:profileId/home"
                element={
                  <RequireAuth>
                    <ChildHomeScreen />
                  </RequireAuth>
                }
              />
              <Route
                path="/child/:profileId/worlds"
                element={
                  <RequireAuth>
                    <WorldMapScreen />
                  </RequireAuth>
                }
              />
              <Route
                path="/child/:profileId/worlds/:worldSlug"
                element={
                  <RequireAuth>
                    <LevelSelectScreen />
                  </RequireAuth>
                }
              />
              <Route
                path="/child/:profileId/play/:levelId"
                element={
                  <RequireAuth>
                    <GameplayScreen />
                  </RequireAuth>
                }
              />
              <Route
                path="/child/:profileId/complete/:levelId"
                element={
                  <RequireAuth>
                    <LevelCompleteScreen />
                  </RequireAuth>
                }
              />
              <Route
                path="/child/:profileId/soon/:section"
                element={
                  <RequireAuth>
                    <ComingSoonScreen />
                  </RequireAuth>
                }
              />
              <Route
                path="/child/:profileId/wordbook"
                element={
                  <RequireAuth>
                    <WordBookScreen />
                  </RequireAuth>
                }
              />
              <Route
                path="/child/:profileId/rewards"
                element={
                  <RequireAuth>
                    <RewardsRoomScreen />
                  </RequireAuth>
                }
              />
              <Route
                path="/child/:profileId/daily"
                element={
                  <RequireAuth>
                    <DailyFindScreen />
                  </RequireAuth>
                }
              />
              <Route
                path="/parent/gate"
                element={
                  <RequireAuth>
                    <ParentGateScreen />
                  </RequireAuth>
                }
              />
              <Route
                path="/parent/dashboard"
                element={
                  <RequireAuth>
                    <ParentDashboardShell />
                  </RequireAuth>
                }
              >
                <Route index element={<ParentOverview />} />
                <Route path="profiles" element={<ProfilesManager />} />
                <Route path="progress/:profileId" element={<ProgressDetail />} />
                <Route path="purchases" element={<PurchasesScreen />} />
                <Route path="privacy" element={<PrivacyScreen />} />
                <Route path="support" element={<SupportScreen />} />
              </Route>
              <Route
                path="/admin"
                element={
                  <RequireAuth>
                    <AdminShell />
                  </RequireAuth>
                }
              >
                <Route index element={<AdminOverview />} />
                <Route path="worlds" element={<WorldManager />} />
                <Route path="levels" element={<LevelManager />} />
                <Route path="levels/:id/edit" element={<LevelEditor />} />
                <Route path="debug-previews" element={<DebugPreviews />} />
                <Route path="learning-objects" element={<LearningObjectLibrary />} />
                <Route path="media" element={<MediaLibrary />} />
                <Route path="generate" element={<AiSceneGenerator />} />
                <Route path="support" element={<SupportInbox />} />
                <Route path="audit" element={<AuditLog />} />
                <Route path="backups" element={<BackupsPage />} />
                <Route path="security" element={<SecurityPage />} />
                <Route path="legal-packet" element={<LegalPacketPage />} />
                <Route path="settings" element={<AppSettingsEditor />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <HealthBadge />
          </SettingsProvider>
        </ChildProfileProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
